// @ts-nocheck
/* Starter Sync - BudgetManagementHeader */
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import { BudgetWidgetNames } from "@insite/client-framework/Store/Pages/BudgetManagement/BudgetManagementState";
import setBudgetCalendar from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/SetBudgetCalendar";
import setDisplayedWidgetName from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/SetDisplayedWidgetName";
import updateLoadMaintenanceInfoParameter from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/UpdateLoadMaintenanceInfoParameter";
import updateLoadParameter from "@insite/client-framework/Store/Pages/BudgetManagement/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import { BudgetCalendarModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonProps } from "@insite/mobius/Button";
import { LinkPresentationProps } from "@insite/mobius/Link";
import OrderActionSet, {
    orderActionSetFieldDefinitions,
    OrderActionSetPresentationProps,
} from "@store/Library/Components/Toolkit/Order/OrderActionSet";
import React from "react";
import { connect, ResolveThunks } from "react-redux";

/* Store Custom Code - Start */
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getBudgetCalendarsDataView } from "@insite/client-framework/Store/Data/BudgetCalendars/BudgetCalendarsSelectors";
import { BudgetEnforcementLevel } from "@insite/client-framework/Store/Pages/BudgetManagement/BudgetManagementReducer";
import usePersistentDataView from "@toolkit/Framework/Common/Hooks/usePersistentDataView";

const mapStateToProps = (state: ApplicationState) => {
    return {
        budgetCalendarsDataView: getBudgetCalendarsDataView(state),
        billToState: getCurrentBillToState(state),
    };
};
/* Store Custom Code - End */

const enum fields {
    showAssignBudgets = "showAssignBudgets",
    showConfigureBudget = "showConfigureBudget",
    showCostCodes = "showCostCodes",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAssignBudgets]: boolean;
        [fields.showConfigureBudget]: boolean;
        [fields.showCostCodes]: boolean;
    };
}

const mapDispatchToProps = {
    setDisplayedWidgetName,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>; // Store Custom Code

export interface BudgetActionsStyles {
    actionSet?: OrderActionSetPresentationProps;
    costCodesButton?: ButtonProps;
    assignBudgetsButton?: ButtonProps;
    configureBudgetsButton?: ButtonProps;
    costCodesLink?: LinkPresentationProps;
    assignBudgetsLink?: LinkPresentationProps;
    configureBudgetsLink?: LinkPresentationProps;
}

export const BudgetActionsStyles: BudgetActionsStyles = {
    assignBudgetsButton: { variant: "secondary" },
    costCodesButton: { variant: "secondary" },
};

const styles = BudgetActionsStyles;

const BudgetActions = ({
    fields: widgetFields,
    setDisplayedWidgetName,
    // Store Custom Code
    budgetCalendarsDataView,
    billToState,
}: Props) => {
    const dispatch = useAppDispatch();

    /* Store Custom Code - Start */
    const calendarPersistentDataView = usePersistentDataView(budgetCalendarsDataView);
    const enforcementLevel = !billToState?.isLoading ? billToState.value?.budgetEnforcementLevel || "" : "";
    /* Store Custom Code - End */

    const defaultFiscalYear = new Date().getFullYear();
    const clickCostCodesHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        budgetWidgetNavigationOperations(event, "CostCodes");
    };

    const clickAssignBudgetsHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        budgetWidgetNavigationOperations(event, "AssignBudgets");
        dispatch(updateLoadMaintenanceInfoParameter({ fiscalYear: defaultFiscalYear }));
    };

    const clickConfigureBudgetsHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        budgetWidgetNavigationOperations(event, "ConfigureBudget");
        setBudgetCalendar({
            value: {
                fiscalYear: defaultFiscalYear,
                fiscalYearEndDate: null,
                budgetPeriods: [] as Date[],
            } as BudgetCalendarModel,
        });
    };

    const budgetWidgetNavigationOperations = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        displayedWidgetName: BudgetWidgetNames,
    ): void => {
        event.preventDefault();
        dispatch(setDisplayedWidgetName({ value: displayedWidgetName }));
        dispatch(
            updateLoadParameter({
                userProfileId: "",
                fiscalYear: defaultFiscalYear,
                fullGrid: true,
                shipToId: "",
            }),
        );
    };

    return (
        <OrderActionSet
            {...styles.actionSet}
            fields={widgetFields}
            actions={[
                {
                    key: "configure",
                    label: translate("Configure Budgets"),
                    onClick: clickConfigureBudgetsHandler,
                    buttonStyles: styles.configureBudgetsButton,
                    linkStyles: styles.configureBudgetsLink,
                    testSelector: "configureBudgetsButton",
                    field: fields.showConfigureBudget,
                },
                {
                    key: "assign",
                    label: translate("Assign Budgets"),
                    onClick: clickAssignBudgetsHandler,
                    buttonStyles: styles.assignBudgetsButton,
                    linkStyles: styles.assignBudgetsLink,
                    testSelector: "assignBudgetsButton",
                    field: fields.showAssignBudgets,
                    // Store Custom Code
                    isDisabled:
                        calendarPersistentDataView.value?.length === 0 ||
                        enforcementLevel === BudgetEnforcementLevel.None,
                },
                {
                    key: "costcodes",
                    label: translate("Cost Codes"),
                    onClick: clickCostCodesHandler,
                    buttonStyles: styles.costCodesButton,
                    linkStyles: styles.costCodesLink,
                    testSelector: "costCodesButton",
                    field: fields.showCostCodes,
                },
            ]}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BudgetActions), // Store Custom Code
    definition: {
        group: "BudgetManagement",
        allowedContexts: ["BudgetManagementPage"],
        displayName: "Actions",
        fieldDefinitions: orderActionSetFieldDefinitions([
            { name: fields.showAssignBudgets },
            { name: fields.showConfigureBudget },
            { name: fields.showCostCodes },
        ]),
    },
};

export default widgetModule;
